import { ATTY_REPRESENT_RADIO, HIPPA_RADIO, HIOF_OPTION_RADIO, MM_NO_LONGER_BILLING_RADIO, detailedSearchOptions, filtersFromData, surgicalReviewOutcomesOptions } from "../assets/data"
import { filterActions } from "../store/filter"
import { getItemFromStorage, getItemFromStorageSimple, reduceToIdArray, removeStorageItem, setStorageItem, formatCurrency, formatDateLocal } from "./script"
import { getLast6Months, getLastMonth, getLastWeek, getThisMonth, getThisYear, getThisWeek } from "./dateScripts"
import { localDateOnlyString } from "./dateParsing"
import { attorneyLegalStatusOptions, attorneyStatusesOptions, attorneyStatusUnattached, caseTypeOptions, evaluationTypesOptions, frontendOptions, getNegotiatorType, gdqStatusOptions, medPayMPPolicyStatusOptionsPayerTypes, medPayPIPPolicyStatusOptionsPayerTypes, practicesLocationsGroupedOptions, practicesOptions, preOpTestsOptions, retrievePayerStatuses, retrievePayerTypes, treatmentStatusesOptions, umPolicyStatusOptionsPayerTypes, practicePatientStatusesOptions, MRQStatusesOptions, isCaseTypeOptions, countiesOptions, countiesOptionsGrouped, countiesOptionsWorkQFilters, caseSourcesOptions, attorneyStatusOptionsPayerTypes, retrieveSpecificWorkQ } from "./systemData"
import { miscellaneous } from "./misc"

export const CURRENCY_RANGE_FILTER_KEYS = ['balances', 'total_balances', 'total_charges']

export function parseCurrencyBound(value) {
    if (value === null || value === undefined || value === '') {
        return null
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null
    }
    const raw = `${value}`.trim()
    if (!raw) {
        return null
    }
    // Keep sign for -0.01, -$0.01, $-0.01, and accounting-style (0.01).
    const negative =
        /^\(.*\)$/.test(raw) ||
        /^-/.test(raw.replace(/[\s,$]/g, '')) ||
        /^\$\s*-/.test(raw)
    const s = raw.replace(/[^0-9.]/g, '')
    if (!s) {
        return null
    }
    const n = Number(s)
    if (!Number.isFinite(n)) {
        return null
    }
    return negative ? -n : n
}

export function normalizeCurrencyRange(range, changedBound = null) {
    if (!range || typeof range !== 'object' || Array.isArray(range)) {
        return null
    }
    const min = parseCurrencyBound(range.min)
    const max = parseCurrencyBound(range.max)
    if (min == null && max == null) {
        return null
    }
    let normalizedMin = min
    let normalizedMax = max
    if (normalizedMin != null && normalizedMax != null && normalizedMin > normalizedMax) {
        if (changedBound === 'max') {
            normalizedMin = normalizedMax
        } else {
            normalizedMax = normalizedMin
        }
    }
    const result = {}
    if (normalizedMin != null) {
        result.min = normalizedMin
    }
    if (normalizedMax != null) {
        result.max = normalizedMax
    }
    return result
}

export function isCurrencyRangeFilterValue(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false
    }
    return 'min' in value || 'max' in value
}

export function getCurrencyRangeApiKey(filterKey) {
    if (filterKey === 'balances') {
        return 'balance'
    }
    return filterKey
}

function formatCurrencyBoundLabel(amount) {
    if (amount < 0) {
        return `-$${formatCurrency(Math.abs(amount))}`
    }
    return `$${formatCurrency(amount)}`
}

export function formatCurrencyRangeLabel(range) {
    const normalized = normalizeCurrencyRange(range)
    if (!normalized) {
        return null
    }
    const { min, max } = normalized
    if (min != null && max != null) {
        return `${formatCurrencyBoundLabel(min)} - ${formatCurrencyBoundLabel(max)}`
    }
    if (min != null) {
        return `${formatCurrencyBoundLabel(min)}+`
    }
    if (max != null) {
        return `up to ${formatCurrencyBoundLabel(max)}`
    }
    return null
}

export function isMeaningfulWorkQFilterValue(v) {
    if (v === 0) return true
    if (v === null || v === undefined || v === '') return false
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'object') {
        if ('value' in v) return isMeaningfulWorkQFilterValue(v.value)
        if (isCurrencyRangeFilterValue(v)) {
            return normalizeCurrencyRange(v) != null
        }
        if (isIncidentDateOperatorFilterValue(v)) {
            return normalizeIncidentDateFilterValue(v) != null
        }
        return Object.keys(v).length > 0
    }
    return !!v
}

export function isIncidentDateOperatorFilterValue(v) {
    return !!(v && typeof v === 'object' && !Array.isArray(v) && ('op' in v || 'date' in v || 'start' in v || 'end' in v))
}

export function normalizeIncidentDateFilterValue(value) {
    if (value === null || value === undefined || value === '') {
        return null
    }

    if (typeof value === 'string') {
        return value
    }

    if (!isIncidentDateOperatorFilterValue(value)) {
        return null
    }

    const op = String(value.op || '').toLowerCase()

    if (op === 'ge' || op === 'le') {
        if (!value.date) {
            return null
        }
        return { op, date: value.date }
    }

    if (op === 'range') {
        if (!value.start || !value.end) {
            return null
        }
        return { op: 'range', start: value.start, end: value.end }
    }

    return null
}

export function formatIncidentDateFilterLabel(value) {
    if (typeof value === 'string') {
        if (value.includes('|')) {
            const [start, end] = value.split('|')
            return `${formatDateLocal(start)} - ${formatDateLocal(end)}`
        }
        return formatDateLocal(value)
    }

    const normalized = normalizeIncidentDateFilterValue(value)
    if (!normalized || typeof normalized === 'string') {
        return typeof normalized === 'string' ? formatIncidentDateFilterLabel(normalized) : null
    }

    if (normalized.op === 'ge') {
        return `≥ ${formatDateLocal(normalized.date)}`
    }
    if (normalized.op === 'le') {
        return `≤ ${formatDateLocal(normalized.date)}`
    }
    if (normalized.op === 'range') {
        if (normalized.start === normalized.end) {
            return formatDateLocal(normalized.start)
        }
        return `${formatDateLocal(normalized.start)} - ${formatDateLocal(normalized.end)}`
    }

    return null
}

export function applyFilterEntryToWorkQBody(body, key, filters) {
    if (CURRENCY_RANGE_FILTER_KEYS.includes(key) && isCurrencyRangeFilterValue(filters[key])) {
        const normalized = normalizeCurrencyRange(filters[key])
        if (normalized) {
            body[getCurrencyRangeApiKey(key)] = normalized
        }
        return true
    }
    if (key === 'incident_date' || key === 'mrq_entry_date' || key === 'gdq_exit_date') {
        const normalized = normalizeIncidentDateFilterValue(filters[key])
        if (normalized) {
            body[key] = normalized
        }
        return true
    }
    if (key === 'hippa' || key === 'hiof_selected') {
        let raw = filters[key]
        if (raw && typeof raw === 'object' && !Array.isArray(raw) && 'value' in raw) {
            raw = raw.value
        }
        if (raw === 0 || raw === '0') {
            body[key] = 0
        } else if (raw === 1 || raw === '1') {
            body[key] = 1
        }
        // null / 'all' / omitted — All entries, do not send the filter
        return true
    }
    return false
}

export function selectFiltersBasedOnKey(key) {
    switch (key) {
        ////////////
        case 'first_name':
            return {
                type: 'direct',
            }
        ///////
        case 'master_status':
            return {
                type: 'select',
                // options: 'frontend_options.MASTER_STATUS'
                options: frontendOptions()?.MASTER_STATUS
            }
        case 'case_type':
            return {
                type: 'multiple_select',
                key: key,
                // options: 'case_types'
                options: caseTypeOptions()
            } 
        case 'case_source':
            return {
                type: 'multiple_select',
                key: key,
                // options: 'case_types'
                options: caseSourcesOptions()
            }  
        case 'case_type_category':
            return {
                type: 'multiple_select',
                key: key,
                options: frontendOptions()?.CASE_TYPE_CATEGORY_OPTIONS 
            } 
        case 'gdq_status':
            return {
                type: 'select',
                key: key,
                options: gdqStatusOptions()?.filter(item => item.is_gdq) 
            }      
        case 'gdq_status_mrq':
            return {
                type: 'select',
                key: key,
                options: gdqStatusOptions()?.filter(item => item.is_exit_status) 
            } 
        case 'mrq_status':
            return {
                type: 'multiple_select',
                key: key,
                options: MRQStatusesOptions(true),
                hasRemove: true
            }     
        case 'treatment_year':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'initial_treatment_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'incident_date':
            return {
                type: 'date_operator',
                key: key,
                options: null
            }
        case 'fast_help_assigned_at':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'days_payer_type':
            return {
                type: 'number',
                key: key,
                options: null
            }
        case 'days_payer_status':
            return {
                type: 'number',
                key: key,
                options: null
            }
        case 'days_fast_help_assigned':
            return {
                type: 'number',
                key: key,
                options: null
            }
        case 'treatment_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'kept_appointment_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'missed_appointment_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'next_appointment_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'mrq_entry_date':
            return {
                type: 'date_operator',
                key: key,
                options: null
            }
        case 'gdq_exit_date':
            return {
                type: 'date_operator',
                key: key,
                options: null
            }
        case 'last_fu_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'next_fu_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'insurance_carrier_global':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'insurance-carriers'
            }
        case 'payer_type_filters':
            return {
                type: 'select',
                key: key,
                options: retrievePayerTypes(),
                hasChild: {
                    key: 'payer_status',
                    type: 'multiple_select',
                    options: null,
                }
            }
        case 'pt_filter': 
            return {
                type: 'multiple_select',
                key: key,
                // options: retrievePayerTypes(),
                options: [
                    ...retrievePayerTypes(),
                    {
                        label: 'Undetermined',
                        value: 0
                    }
                ],
            }
        case 'ps_filter': 
            return {
                type: 'multiple_select',
                key: key,
                // options: retrievePayerStatuses(),
                options: [
                    ...retrievePayerStatuses(),
                    {
                        label: 'Undetermined',
                        value: 0
                    }
                ],
            }
        case 'ps_filter_global': 
            return {
                type: 'multiple_select',
                key: key,
                options: retrievePayerStatuses(),
            }
        // case 'payer_type':
        //     return {
        //         type: 'select',
        //         key: key,
        //         options: retrievePayerTypes(),
        //         hasChild: {
        //             key: 'payer_status',
        //             type: 'multiple_select',
        //             options: null,
        //         }
        //     }
        // case 'payer_status': 
        //     return {
        //         type: 'multiple_select',
        //         key: key,
        //         options: retrievePayerTypes(),
        //         disabled: true
        //     }
        case 'discharged_status':
            return {
                type: 'multiple_select',
                key: key,
                options: filtersFromData.discharge_status
            }
        
        case 'payer_type_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'payer_status_date': 
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'transportation_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'show_deny_further_trips':
            return {
                type: 'checkbox',
                key: key,
                options: null
            }
        case 'assigned_task_cases':
            return {
                type: 'checkbox',
                key: key,
                options: null
            }
        case 'is_case':
            return {
                type: 'radio',
                key: key,
                options: isCaseTypeOptions()
            }
        case 'future_appointment':
            return {
                type: 'radio',
                key: key,
                options: isCaseTypeOptions()
            }
        case 'negotiation_type':
            return {
                type: 'multiple_select',
                key: key,
                options: frontendOptions()?.negotiationsTypeOptions
            }
        case 'neg_status':
            return {
                type: 'multiple_select',
                key: key,
                options: frontendOptions()?.NEG_STATUS
            }
        case 'practice':
            return {
                type: 'select',
                key: key,
                options: practicesOptions(),
            }
        case 'location':
            return {
                type: 'group_select_multiple',
                // type: 'group_select',
                key: key,
                // options: 'practices/locations'
                options: practicesLocationsGroupedOptions()
            }  
        case 'practice_treatment_status':
            return {
            type: 'multiple_select',
            key: key,
            options: practicePatientStatusesOptions(true)
        }
        case 'aica_department_status':
            return {
            type: 'multiple_select',
            key: key,
            options: treatmentStatusesOptions(true)
        }
        case 'practice_patient_status_filters':
            return {
                type: 'practice_patient_status_repeater',
                key: key,
                options: {
                    practices: practicesOptions(),
                    statuses: practicePatientStatusesOptions(true),
                    matchOptions: [
                        { label: 'AND', value: 'and' },
                        { label: 'OR', value: 'or' },
                    ],
                },
            }
        case 'practice_initial_treatment_date':
            return {
            type: 'range',
            key: key,
            options: null
        }
        case 'lien_created_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'lien_notice_date':
            return {
                type: 'range',
                key: key,
                options: null
            }  
        case 'lien_status':
            return {
                type: 'multiple_select',
                key: key,
                options: masterOptionsFunction('lien_statuses') || filtersFromData.lien_status
            }
        case 'lien_stage':
            return {
                type: 'multiple_select',
                key: key,
                options: masterOptionsFunction('lien_stages') || filtersFromData.lien_stage
            }
        case 'lien_original_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'lien_amended_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'lien_satisfied_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'patient_county':
            return {
                // type: 'group_select_multiple',
                type: 'multiple_select',
                key: key,
                options: countiesOptionsWorkQFilters('GA')
                // options: countiesOptionsGrouped()
            }
        case 'attorney':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'law-firm-attorneys'
            }
        case 'attorney_letter':
            return {
                type: 'text',
                key: key,
                options: null,
            }
        case 'case_manager':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'law-firm-case-managers'
            }
        case 'attorney_collection_rep':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'collections',
                iscollection: true
            }
        case 'attorney_poc_rep':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'poc',
                iscollection: true
            }
        case 'attorney_poc_user':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'poc',
                iscollection: true
            }
        case 'law_firm':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'law-firms'
            }
        case 'attorney_payer_status':
            return {
                type: 'multiple_select',
                key: key,
                options: attorneyStatusOptionsPayerTypes(),
                direct: true
            }
        case 'attorney_status':
            return {
                type: 'multiple_select',
                key: key,
                // Include both `show_in_attorney_status` and `show_in_no_atty_reason` statuses
                // so users can filter for "No Atty on File" reasons too. The backend routes the
                // selected ids by case state (active managing attorney -> case_attorneys.status_id,
                // otherwise -> case_additionals.no_attorney_reason).
                options: (() => {
                    const seen = new Set()
                    return [
                        ...(attorneyStatusesOptions() ?? []),
                        ...(attorneyStatusUnattached() ?? []),
                    ].filter(opt => {
                        if (seen.has(opt.value)) return false
                        seen.add(opt.value)
                        return true
                    })
                })(),
            }
        case 'attorney_legal_status':
            return {
                type: 'multiple_select',
                key: key,
                options: attorneyLegalStatusOptions(),
            }
        case 'attorney_status_type':
            return {
                type: 'select',
                key: key,
                options: frontendOptions()?.ATTORNEY_STATUS_TYPE,
            }
        case 'attorney_represent':
            return {
                type: 'radio',
                key: key,
                options: ATTY_REPRESENT_RADIO,
            }               
        case 'out_of_state':
            return {
                type: 'radio',
                key: key,
                // options: 'frontend_options.OutOfStateRadioOptions'
                options: frontendOptions()?.OutOfStateRadioOptions
            }
        case 'hippa':
            return {
                type: 'radio',
                key: key,
                options: HIPPA_RADIO,
            }
        case 'hiof_selected':
            return {
                type: 'radio',
                key: key,
                options: HIOF_OPTION_RADIO,
            }
        case 'mm_no_longer_billing':
            return {
                type: 'radio',
                key: key,
                options: MM_NO_LONGER_BILLING_RADIO,
            }
        // case 'ihc_review':
        //     return {
        //         type: 'checkbox',
        //         key: key,
        //         options: null,
        //     }
        case 'do_not_lien':
            return {
                type: 'checkbox',
                key: key,
                options: null,
            }
        case 'negotiator_type':
            return {
                type: 'select',
                key: key,
                options: getNegotiatorType()
            }
        case 'liability_carrier':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'insurance-carriers'
            }
        case 'liability_adjuster':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'insurance-carrier-adjusters'
            }
        case 'liability_status':
            return {
                type: 'multiple_select',
                key: key,
                // options: 'frontend_options.assumedOptions',
                options: frontendOptions()?.assumedOptions,
                // structure_is_object: true
            }
        case 'major_med_carrier':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'insurance-carriers'
            }
        case 'major_med_adjuster':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'insurance-carrier-adjusters'
            }
        case 'mp_pip_um_carrier':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'insurance-carriers'
            }
        case 'mp_pip_um_adjuster':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'insurance-carrier-adjusters'
            }
        case 'um_status':
            return {
                type: 'multiple_select',
                key: key,
                options: umPolicyStatusOptionsPayerTypes(),
                direct: true
            }
        case 'mp_status':
            return {
                type: 'multiple_select',
                key: key,
                options: medPayMPPolicyStatusOptionsPayerTypes(),
                direct: true
            }
        case 'pip_status':
            return {
                type: 'multiple_select',
                key: key,
                options: medPayPIPPolicyStatusOptionsPayerTypes(),
                direct: true
            }
        case 'worker_comp_carrier':
            return {
                type: 'select_async',
                key: key,
                options: null,
                path: 'insurance-carriers'
            }
        case 'worker_comp_type':
            return {
                type: 'select',
                key: key,
                // options: 'from_assets|worker_comp_type',
                options: filtersFromData.worker_comp_type
            }
        case 'provider':
            return {
                type: 'text',
                key: key,
                options: null,
            }
        case 'area':
            return {
                type: 'text',
                key: key,
                options: null,
            }
        case 'surgical_stage':
            return {
                type: 'select',
                key: key,
                // options: 'from_assets|surgical_stage',
                options: filtersFromData.surgical_stage
            }
        case 'referring_provider':
            return {
                type: 'text',
                key: key,
                options: null,
            }
        case 'evaluation_provider':
            return {
                type: 'text',
                key: key,
                options: null,
            }
        case 'surgical_provider':
            return {
                type: 'text',
                key: key,
                options: null,
            }
        case 'surgical_area':
            return {
                type: 'text',
                key: key,
                options: null,
            }
        case 'preauthorization_required':
            return {
                type: 'checkbox',
                key: key,
                options: null,
            }
        case 'evaluation_appointment_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'evaluation_type':
            return {
                type: 'select',
                key: key,
                // options: 'from_assets|surgical_stage',
                options: evaluationTypesOptions()
            }
        case 'surgery_scheduled_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'surgery_performed_date':
            return {
                type: 'range',
                key: key,
                options: null
            }
        case 'surgical_billing_status':
            return {
                type: 'multiple_select',
                key: key,
                options: frontendOptions()?.surgeryChargedStatus,
                direct: true
            }
        case 'surgical_review_outcomes':
            return {
                type: 'select',
                key: key,
                options: surgicalReviewOutcomesOptions,
                direct: true
            }
        case 'pre_op_test':
            return {
                type: 'checkbox_multi_array',
                key: key,
                options: preOpTestsOptions(),
            }

        case 'evaluation_date_ordered':
            return {
                type: 'range',
                key: key,
                options: null
            }
            ////////////////////////////////////////////////////////
        case 'payment_plan_requires_approval':
            return {
                type: 'checkbox',
                key: key,
                options: null,
            }
        case 'payment_plan_requires_attention':
            return {
                type: 'checkbox',
                key: key,
                options: null,
            }
        case 'payment_plan_send_to_payment':
            return {
                type: 'checkbox',
                key: key,
                options: null,
            }
        case 'balances':
        case 'total_balances':
        case 'total_charges':
            return {
                type: 'currency_range',
                key: key,
                options: null,
            }
        default:
            return null
    }
}


export function masterOptionsFunction(filterString, itemData) {
    const systemSettings = getItemFromStorage('settings')
    if (itemData?.direct) {
        return itemData.options
    }
    if (filterString) {
        if (filterString.includes("/")) {
            let arr = filterString.split('/')        

            
            if (systemSettings && systemSettings[arr[0]]) {        
                let data = []
                systemSettings[arr[0]].forEach(element => {
                    data.push({
                        label: element.name,
                        value: element.id,
                        data: element,
                        isDisabled: element.active == 0 ? 1 : 0,
                        ...element
                    })
                });
                let secondaryArray = []
                data.forEach(item => {
                    let opt = {
                        label: item.label,
                        options: []
                    }
                    if (item.data.locations) {
                        item.data.locations.forEach(element => {
                            opt.options.push({
                                label: element.code, //{/* display_name */}
                                value: element.id,
                            })
                        })
                        
                    }



                    secondaryArray.push(opt)
                })

                return secondaryArray
            }

            return null
            
        } else if(filterString.includes("|")){
            let arr = filterString.split('|')

            if (filtersFromData && filtersFromData[arr[1]]) {
                return filtersFromData[arr[1]]
            }
        } else {
            let arr = filterString.split('.')
            if (arr.length > 1) {

                let data = {}
                if (systemSettings && systemSettings[arr[0]]) {        
                    for (const [key, value] of Object.entries(systemSettings[arr[0]])) {  
                        data[key] = value
                    }

                    return data[arr[1]]
                }

                return null
            } else {
                if (itemData && itemData.structure_is_object) {
                    let data = []
                    for (const [key, value] of Object.entries(systemSettings[arr[0]])) {               
                        data.push({
                            label: value,
                            value: key
                        })
                    } 

                    return data
                } else {
                    let data = []  
                    if (systemSettings && systemSettings[arr[0]]) {        
                        systemSettings[arr[0]].forEach(element => {
                            data.push({
                                label: element.name,
                                value: element.id,
                                isDisabled: element.active == 0 ? 1 : 0,
                                ...element
                            })
                        });

                        return data
                    }
                }

                return null
            }
        }
    }
}


function stableStringifyForFingerprint(value) {
    if (value === null || value === undefined) {
        return String(value)
    }
    if (typeof value !== 'object') {
        return JSON.stringify(value)
    }
    if (Array.isArray(value)) {
        return `[${value.map((v) => stableStringifyForFingerprint(v)).join(',')}]`
    }
    const keys = Object.keys(value).sort()
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringifyForFingerprint(value[k])}`).join(',')}}`
}

/** Stable fingerprint so local persisted filters can be reconciled when backend `filter_conditions` changes. */
export function fingerprintFilterConditions(filter_conditions) {
    if (filter_conditions == null || typeof filter_conditions !== 'object') {
        return ''
    }
    return stableStringifyForFingerprint(filter_conditions)
}

export function workQFilterConditionsSigStorageKey(workQSlug) {
    return `${workQSlug}__wq_fc_sig`
}

/**
 * Builds filter values from work-queue `filter_conditions` for the given Nova types.
 * Type 2 = Flexible defaults. Type 1 = Enforce values.
 */
export function computeWorkQFiltersFromConditionsByTypes(filter_conditions, userLocations, types) {
    if (!filter_conditions) {
        return {}
    }
    const typeSet = new Set((types || []).map((t) => Number(t)))
    const newObj = {}
    for (const [key, value] of Object.entries(filter_conditions)) {
        if (key.includes('_filter_type')) {
            if (typeSet.has(Number(value))) {
                const a = key.split('_filter_type')

                if (a[0].includes('_date') || a[0] === 'fast_help_assigned_at') {
                    const keyType = filter_conditions[a[0]]?.type

                    // Date operator filters (Incident / MRQ / GDQ Exit): seed GE/LE for the WorkQ filter UI.
                    if ((a[0] === 'incident_date' || a[0] === 'mrq_entry_date' || a[0] === 'gdq_exit_date') && (keyType === 'ge' || keyType === 'le')) {
                        const date = filter_conditions[a[0]]?.date
                        if (date) {
                            newObj[a[0]] = { op: keyType, date }
                        }
                    } else if (keyType) {
                        switch (keyType) {
                            case 'today': {
                                const today = localDateOnlyString(new Date())
                                newObj[a[0]] = `${today}|${today}`
                                break
                            }
                            case 'tomorrow': {
                                const t = new Date()
                                t.setDate(t.getDate() + 1)
                                const tomorrow = localDateOnlyString(t)
                                newObj[a[0]] = `${tomorrow}|${tomorrow}`
                                break
                            }
                            case 'this_week':
                                newObj[a[0]] = getThisWeek(new Date())
                                break
                            case 'last_week':
                                newObj[a[0]] = getLastWeek(new Date())
                                break
                            case 'this_month':
                                newObj[a[0]] = getThisMonth(new Date())
                                break
                            case 'last_month':
                                newObj[a[0]] = getLastMonth()
                                break
                            case 'last_6_month':
                                newObj[a[0]] = getLast6Months()
                                break
                            case 'this_year':
                                newObj[a[0]] = getThisYear(new Date())
                                break
                            case 'last_year': {
                                const y = new Date().getFullYear() - 1
                                newObj[a[0]] = `${y}-01-01|${y}-12-31`
                                break
                            }
                            case 'range': {
                                const cfg = filter_conditions[a[0]] || {}
                                const fromDay = cfg.from_day ?? cfg.from
                                const toDay = cfg.to_day ?? cfg.to
                                if (fromDay === null || fromDay === undefined || fromDay === ''
                                    || toDay === null || toDay === undefined || toDay === '') {
                                    break
                                }
                                const startDate = new Date()
                                startDate.setDate(startDate.getDate() + Number(fromDay))
                                const endDate = new Date()
                                endDate.setDate(endDate.getDate() + Number(toDay))
                                newObj[a[0]] = `${localDateOnlyString(startDate)}|${localDateOnlyString(endDate)}`
                                break
                            }
                            default:
                                break
                        }
                    }
                } else {
                    newObj[a[0]] = filter_conditions[a[0]]
                    if (a[0] === 'practice_patient_status_filters') {
                        newObj.practice_patient_status_filters_match = filter_conditions.practice_patient_status_filters_match || 'and'
                    }
                    if ((a[0] === 'hippa' || a[0] === 'hiof_selected') && (newObj[a[0]] === null || newObj[a[0]] === undefined || newObj[a[0]] === '')) {
                        delete newObj[a[0]]
                    }
                }
            }
        }
    }

    if (userLocations && userLocations.length) {
        const reducedLocations = reduceToIdArray(userLocations)
        if (newObj.locations) {
            newObj.location = [...newObj.locations, ...reducedLocations]
        } else {
            newObj.location = reducedLocations
        }
    }

    return newObj
}

export function computeWorkQDefaultFiltersFromConditions(filter_conditions, userLocations) {
    return computeWorkQFiltersFromConditionsByTypes(filter_conditions, userLocations, [2])
}

export function checkAndSetFiltersForWorkQsV2(filter_conditions, dispatch, filters, userLocations) {
    if (!filter_conditions) {
        return undefined
    }
    const newObj = computeWorkQDefaultFiltersFromConditions(filter_conditions, userLocations)

    if ((!filters || !miscellaneous.numberOfObjectValues(filters)) && (newObj && miscellaneous.numberOfObjectValues(newObj))) {
        if (dispatch) {
            dispatch(filterActions.setFiltersMultiple(newObj))
        }

        return newObj
    }

    return undefined
}

function flattenMetaOptionsToValueSet(meta) {
    if (!meta || !meta.options) {
        return null
    }
    const opts = meta.options
    if (!Array.isArray(opts) || opts.length === 0) {
        return null
    }
    const set = new Set()
    if (opts[0] && Array.isArray(opts[0].options)) {
        for (const grp of opts) {
            if (grp?.options && Array.isArray(grp.options)) {
                for (const opt of grp.options) {
                    if (opt && opt.value != null) {
                        set.add(opt.value)
                    }
                }
            }
        }
    } else {
        for (const opt of opts) {
            if (opt && opt.value != null) {
                set.add(opt.value)
            }
        }
    }
    return set.size ? set : null
}

function extractComparableFilterValue(entry) {
    if (entry == null) {
        return null
    }
    if (typeof entry === 'object' && !Array.isArray(entry)) {
        if (entry.value != null && entry.value !== '') {
            return entry.value
        }
        if (entry.item?.id != null) {
            return entry.item.id
        }
    }
    return entry
}

function valueInPrimitiveSet(v, allowSet) {
    if (allowSet.has(v)) {
        return true
    }
    const n = Number(v)
    if (!Number.isNaN(n) && allowSet.has(n)) {
        return true
    }
    for (const allowed of allowSet) {
        if (allowed === v) {
            return true
        }
        if (String(allowed) === String(v)) {
            return true
        }
        if (Number(allowed) === Number(v) && !Number.isNaN(Number(allowed)) && !Number.isNaN(Number(v))) {
            return true
        }
    }
    return false
}

function getRigidAllowSetForFilterKey(filterKey, filter_conditions) {
    const t = filter_conditions[`${filterKey}_filter_type`]
    if (t !== 1) {
        return null
    }
    const list = filter_conditions[filterKey]
    if (!Array.isArray(list)) {
        return null
    }
    return new Set(list)
}

/**
 * When `filter_conditions` changed, drop persisted options that are no longer valid (rigid allowlist
 * or known static options from selectFiltersBasedOnKey). Keeps valid selections only.
 */
export function prunePersistedFilterValue(filterKey, value, workq) {
    const fc = workq?.filter_conditions
    if (!fc) {
        return value
    }

    const rigidSet = getRigidAllowSetForFilterKey(filterKey, fc)
    if (rigidSet && rigidSet.size) {
        if (Array.isArray(value)) {
            return value.filter((entry) => {
                const v = extractComparableFilterValue(entry)
                return v != null && valueInPrimitiveSet(v, rigidSet)
            })
        }
        const v = extractComparableFilterValue(value)
        if (v == null || !valueInPrimitiveSet(v, rigidSet)) {
            return undefined
        }
        return value
    }

    const meta = selectFiltersBasedOnKey(filterKey)
    const optionSet = flattenMetaOptionsToValueSet(meta)
    if (optionSet && optionSet.size > 0) {
        if (Array.isArray(value)) {
            return value.filter((entry) => {
                const v = extractComparableFilterValue(entry)
                return v != null && valueInPrimitiveSet(v, optionSet)
            })
        }
        const v = extractComparableFilterValue(value)
        if (v != null && !valueInPrimitiveSet(v, optionSet)) {
            return undefined
        }
        return value
    }

    return value
}

/**
 * Merges localStorage work-queue filters with backend `filter_conditions`:
 * - No persisted filters → same as empty branch (defaults when Redux filters empty).
 * - Fingerprint match → restore persisted (existing behavior).
 * - Fingerprint mismatch → recompute defaults, prune persisted values per key, merge, persist, dispatch.
 */
export function resolveWorkQueueFiltersFromStorageAndConditions({
    workqSlug,
    workq,
    dispatch,
    reduxFilters,
    userLocations,
}) {
    if (!workq?.filter_conditions) {
        return {}
    }

    const sigKey = workQFilterConditionsSigStorageKey(workqSlug)
    let storedSig = getItemFromStorageSimple(sigKey)
    const currentSig = fingerprintFilterConditions(workq.filter_conditions)

    const persisted = getItemFromStorageSimple(workqSlug)
    const hasPersisted =
        persisted && typeof persisted === 'object' && miscellaneous.numberOfObjectValues(persisted) > 0

    if (!hasPersisted) {
        const applied = checkAndSetFiltersForWorkQsV2(
            workq.filter_conditions,
            dispatch,
            reduxFilters,
            userLocations,
        )
        if (applied && typeof applied === 'object') {
            return applied
        }
        return {}
    }

    if (storedSig == null || storedSig === '') {
        setStorageItem(sigKey, currentSig)
        storedSig = currentSig
    }

    if (storedSig === currentSig) {
        return checkAndSetFiltersForWorkQsV2Persistent(workqSlug, dispatch) || {}
    }

    const defaults = computeWorkQDefaultFiltersFromConditions(workq.filter_conditions, userLocations)
    const kept = {}
    for (const [key, value] of Object.entries(persisted)) {
        if (key === 'payer_type_filters') {
            kept[key] = value
            continue
        }
        const pruned = prunePersistedFilterValue(key, value, workq)
        if (pruned === undefined || pruned === null) {
            continue
        }
        if (Array.isArray(pruned) && pruned.length === 0) {
            continue
        }
        kept[key] = pruned
    }

    const merged = { ...defaults, ...kept }
    if (dispatch) {
        dispatch(filterActions.setFiltersMultiple(merged))
    }
    setStorageItem(workqSlug, merged)
    setStorageItem(sigKey, currentSig)
    return merged
}


export function checkAndSetUserLocationsFiltersForWorkQsV2(locations, dispatch, filters) {
    if (locations) {
        let newObj = {
            key: "location",
            multiple: true,
            value: locations
        }
        if ((!filters || !miscellaneous.numberOfObjectValues(filters)) && (newObj && miscellaneous.numberOfObjectValues(newObj))) {
            if (dispatch) {
                dispatch(filterActions.setFiltersMultiple(newObj))
            }
        }
        
    }
}


export function checkAndSetExceptionsForWorkQs(exceptions_filters, dispatch, exceptions) {
    if (exceptions_filters) {
        let newObj = {
            case_flags: {}
        }
        for (const [key, value] of Object.entries(exceptions_filters)) {
            if (value) {
                newObj.case_flags[key] = value      
            }
        }

        if ((!exceptions || !miscellaneous.numberOfObjectValues(exceptions)) && (newObj && miscellaneous.numberOfObjectValues(newObj))) {
            if (dispatch) {
                dispatch(filterActions.setExceptionsBatch(newObj))
            }

            return newObj
        }
        
    }
}


export function setFiltersInLocalStorage(filters, workQSlug) {
    if (filters && workQSlug) {
        setStorageItem(workQSlug, filters)
        const workq = retrieveSpecificWorkQ(workQSlug)
        setStorageItem(
            workQFilterConditionsSigStorageKey(workQSlug),
            fingerprintFilterConditions(workq?.filter_conditions),
        )
    }
}

const EMPTY_SAVED_FILTER_SLUGS = new Set(['police-report', 'invalid-fax-q'])

export function allowsEmptySavedWorkQueueFilters(workQSlug) {
    return EMPTY_SAVED_FILTER_SLUGS.has(workQSlug)
}

/** Stored filter choice for this queue. Police Report and Invalid Fax treat `{}` as an explicit empty set. */
export function hasPersistedWorkQFilterChoice(workQSlug) {
    const persisted = getItemFromStorageSimple(workQSlug)
    if (!persisted || typeof persisted !== 'object' || Array.isArray(persisted)) {
        return false
    }
    if (allowsEmptySavedWorkQueueFilters(workQSlug)) {
        return true
    }
    return Object.keys(persisted).length > 0
}

/** Hold the first list request until saved views arrive, so an empty saved set is not replaced by defaults. */
export function shouldDeferWorkQListForSavedFilters(workQSlug, savedViewsLoaded) {
    if (!allowsEmptySavedWorkQueueFilters(workQSlug) || savedViewsLoaded) {
        return false
    }
    return !hasPersistedWorkQFilterChoice(workQSlug)
}

const SAVED_VIEW_STRIP_KEYS = [
    'search',
    'search_global',
    'first_name',
    'last_name',
    'dob',
    'ng_number',
    'chart_number',
    'page',
    'per_page',
    'order_by',
    'order_by_direction',
    'widget',
]

export function getWorkQEnforceFilterKeys(filter_conditions) {
    if (!filter_conditions || typeof filter_conditions !== 'object') {
        return []
    }
    return Object.keys(filter_conditions)
        .filter((key) => key.includes('_filter_type') && Number(filter_conditions[key]) === 1)
        .map((key) => key.split('_filter_type')[0])
        .filter(Boolean)
}

export function sanitizeFiltersForSavedView(filters, workq) {
    if (!filters || typeof filters !== 'object' || Array.isArray(filters)) {
        return {}
    }
    const enforceKeys = new Set(getWorkQEnforceFilterKeys(workq?.filter_conditions))
    // Saved views should store only the same keys that we consider "persistent mega-filters".
    // Free-text / detailed-search UI values are stripped (same intent as localStorage persistence).
    const searchKeys = new Set(
        (detailedSearchOptions || [])
            .map((option) => option.apiProp)
            .filter((key) => key)
    )
    const clean = {}
    for (const [key, value] of Object.entries(filters)) {
        if (!key) {
            continue
        }
        if (SAVED_VIEW_STRIP_KEYS.includes(key) || searchKeys.has(key)) {
            continue
        }
        if (enforceKeys.has(key)) {
            continue
        }
        if (!isMeaningfulWorkQFilterValue(value) && value !== 0) {
            continue
        }
        clean[key] = value
    }
    return clean
}

export function sanitizeExceptionsForSavedView(exceptions) {
    if (!exceptions || typeof exceptions !== 'object' || Array.isArray(exceptions)) {
        return {}
    }
    const flags = exceptions.case_flags && typeof exceptions.case_flags === 'object'
        ? exceptions.case_flags
        : exceptions
    const cleanFlags = {}
    for (const [key, value] of Object.entries(flags || {})) {
        if (!key || key === 'case_flags') {
            continue
        }
        if (value) {
            cleanFlags[key] = value
        }
    }
    if (!Object.keys(cleanFlags).length) {
        return {}
    }
    return { case_flags: cleanFlags }
}

export function mergeEnforceFiltersIntoView(filters, workq) {
    const enforce = computeWorkQFiltersFromConditionsByTypes(workq?.filter_conditions, null, [1])
    return { ...(filters || {}), ...(enforce || {}) }
}

function pruneSavedViewFilters(filters, workq) {
    if (!filters || typeof filters !== 'object') {
        return {}
    }
    const kept = {}
    for (const [key, value] of Object.entries(filters)) {
        if (key === 'payer_type_filters') {
            kept[key] = value
            continue
        }
        const pruned = prunePersistedFilterValue(key, value, workq)
        if (pruned === undefined || pruned === null) {
            continue
        }
        if (Array.isArray(pruned) && pruned.length === 0) {
            continue
        }
        if (!isMeaningfulWorkQFilterValue(pruned) && pruned !== 0) {
            continue
        }
        kept[key] = pruned
    }
    return kept
}

export function applySavedWorkQueueView(slug, savedView, workq, dispatch) {
    let filters = sanitizeFiltersForSavedView(savedView?.filters, workq)
    filters = pruneSavedViewFilters(filters, workq)

    if (dispatch) {
        dispatch(filterActions.setFiltersMultiple(filters))
        dispatch(filterActions.setActiveSavedViewSlug(slug || null))
    }

    const hasSavedExceptions = savedView?.exceptions && miscellaneous.numberOfObjectValues(savedView.exceptions)
    const appliedExceptions = hasSavedExceptions
        ? sanitizeExceptionsForSavedView(savedView.exceptions)
        : {}
    if (dispatch) {
        dispatch(filterActions.setExceptionsBatch(appliedExceptions))
    }

    if (slug) {
        setFiltersInLocalStorage(filters, slug)
    }

    return { filters, exceptions: appliedExceptions }
}

export function applyStandardWorkQueueDefaults(workq, dispatch, userLocations) {
    const defaults = computeWorkQDefaultFiltersFromConditions(workq?.filter_conditions, userLocations) || {}
    if (dispatch) {
        dispatch(filterActions.setFiltersMultiple(defaults))
        dispatch(filterActions.setActiveSavedViewSlug(null))
    }
    const appliedExceptions = checkAndSetExceptionsForWorkQs(workq?.exceptions_filters, null, null) || {}
    if (dispatch) {
        dispatch(filterActions.setExceptionsBatch(appliedExceptions))
    }
    if (workq?.slug) {
        setFiltersInLocalStorage(defaults, workq.slug)
    }
    return defaults
}

export function savedViewsAreEqual(left, right) {
    try {
        return JSON.stringify(left || {}) === JSON.stringify(right || {})
    } catch (e) {
        return false
    }
}

export function currentMatchesSavedWorkQueueView(workq, filters, exceptions, savedView) {
    if (!savedView) {
        return false
    }
    const currentFilters = sanitizeFiltersForSavedView(filters, workq)
    const savedFilters = sanitizeFiltersForSavedView(savedView.filters, workq)
    if (!savedViewsAreEqual(currentFilters, savedFilters)) {
        return false
    }

    const currentExceptions = sanitizeExceptionsForSavedView(exceptions)
    const savedExceptions = sanitizeExceptionsForSavedView(savedView.exceptions)
    return savedViewsAreEqual(currentExceptions, savedExceptions)
}

export function savedViewDiffersFromWorkQDefaults(workq, savedView, userLocations) {
    if (!workq || !savedView) {
        return false
    }

    const defaultFilters = sanitizeFiltersForSavedView(
        computeWorkQDefaultFiltersFromConditions(workq?.filter_conditions, userLocations) || {},
        workq,
    )
    const savedFilters = sanitizeFiltersForSavedView(savedView.filters, workq)
    if (!savedViewsAreEqual(defaultFilters, savedFilters)) {
        return true
    }

    const defaultExceptions = sanitizeExceptionsForSavedView(
        checkAndSetExceptionsForWorkQs(workq?.exceptions_filters, null, null) || {},
    )
    const savedExceptions = sanitizeExceptionsForSavedView(savedView.exceptions)
    return !savedViewsAreEqual(defaultExceptions, savedExceptions)
}

export function checkAndSetFiltersForWorkQsV2Persistent(workQSlug, dispatch) {
    if (workQSlug) {
        const localFilters = getItemFromStorageSimple(workQSlug)
        if (!localFilters || typeof localFilters !== 'object') {
            return {}
        }
        if (dispatch) {
            dispatch(filterActions.setFiltersMultiple(localFilters))
        }
        return localFilters
    }

    return {}
}

function filterValueMatchesRemovalCandidate(candidate, filterValue) {
    if (candidate == null) {
        return false
    }
    if (typeof candidate === 'object') {
        return candidate.value == filterValue || candidate.id == filterValue
    }
    return candidate == filterValue
}

export function getFiltersFromLocalStorageAndRemoveSelectedFilter(workQSlug, filterKey, filterValue) {
    if (workQSlug) {
        let filters = getItemFromStorageSimple(workQSlug)
        if (filters && filters[filterKey]) {

            if (Array.isArray(filters[filterKey])) {
                filters[filterKey] = filters[filterKey].filter(v => !filterValueMatchesRemovalCandidate(v, filterValue))
                if (filters[filterKey].length === 0) {
                    delete filters[filterKey]
                }
            } else {
                delete filters[filterKey]
            }
        }
        setStorageItem(workQSlug, filters)
        const workq = retrieveSpecificWorkQ(workQSlug)
        if (workq?.filter_conditions) {
            setStorageItem(
                workQFilterConditionsSigStorageKey(workQSlug),
                fingerprintFilterConditions(workq.filter_conditions),
            )
        }
    }
}


export function getSearchValueFromLocalStorage(workQSlug, filterKeys) {
    if (workQSlug) {
        let filters = getItemFromStorageSimple(workQSlug)
        if (filters && filterKeys.length) {
            for (let i = 0; i < filterKeys.length; i++) {
                let key = filterKeys[i];
                if (filters[key]) {
                    return filters[key]
                }
            }
        }
    }
    return null
}

const WORKQ_SEARCH_SUFFIX = '__wq_search'

export function workQSearchStorageKey(workQSlug) {
    return `${workQSlug}${WORKQ_SEARCH_SUFFIX}`
}

export function persistWorkQSearch(workQSlug, payload) {
    if (!workQSlug || !payload) {
        return
    }
    setStorageItem(workQSearchStorageKey(workQSlug), payload)
}

export function getPersistedWorkQSearchPayload(workQSlug) {
    if (!workQSlug) {
        return null
    }
    const stored = getItemFromStorageSimple(workQSearchStorageKey(workQSlug))
    if (!stored || typeof stored !== 'object') {
        return null
    }
    return stored
}

export function getPersistedWorkQSearchParams(workQSlug) {
    const payload = getPersistedWorkQSearchPayload(workQSlug)
    if (!payload) {
        return null
    }
    if (payload.searchParams && typeof payload.searchParams === 'object') {
        return payload.searchParams
    }
    return payload
}

export function clearPersistedWorkQSearch(workQSlug) {
    if (!workQSlug) {
        return
    }
    removeStorageItem(workQSearchStorageKey(workQSlug))
}

export function resolveEffectiveSearchParams(reduxSearchParams, workQSlug) {
    if (reduxSearchParams && Object.keys(reduxSearchParams).length) {
        return reduxSearchParams
    }
    return getPersistedWorkQSearchParams(workQSlug) || {}
}

export function deriveSearchUIFromParams(searchParams, compoundSearchFromStorage = []) {
    if (!searchParams || !Object.keys(searchParams).length) {
        return { currentValue: '', globalSearch: 0, compoundSearch: [] }
    }

    let compoundSearch = compoundSearchFromStorage?.length
        ? compoundSearchFromStorage
        : detailedSearchOptions.filter(opt => searchParams[opt.apiProp] != null)

    let currentValue = searchParams.search || ''
    if (!currentValue && compoundSearch.length) {
        currentValue = searchParams[compoundSearch[0].apiProp] || ''
    }

    return {
        currentValue,
        globalSearch: searchParams.search_global ? 1 : 0,
        compoundSearch,
    }
}

export function deriveSearchUIFromPersisted(workQSlug, reduxSearchParams) {
    const payload = getPersistedWorkQSearchPayload(workQSlug)
    let searchParams = reduxSearchParams
    let compoundSearch = payload?.compoundSearch || []

    if (!searchParams || !Object.keys(searchParams).length) {
        searchParams = getPersistedWorkQSearchParams(workQSlug)
    }

    return deriveSearchUIFromParams(searchParams, compoundSearch)
}

const WORKQ_FILTER_SIG_SUFFIX = '__wq_fc_sig'

/** Removes every persisted work-queue filter set: each slug key plus its `${slug}__wq_fc_sig` signature key. */
export function clearWorkQFilters() {
    try {
        for (const key of Object.keys(localStorage)) {
            if (key.endsWith(WORKQ_FILTER_SIG_SUFFIX)) {
                removeStorageItem(key.slice(0, -WORKQ_FILTER_SIG_SUFFIX.length))
                removeStorageItem(key)
            }
        }
    } catch (e) {
        // localStorage may be unavailable; ignore
    }
}

/** Clears persisted work-queue filters and the cached recent cases. Used on login. */
export function clearWorkQFiltersAndRecentCases() {
    clearWorkQFilters()
    removeStorageItem('recentCases')
}

const WORKQ_FILTERS_RESET_VERSION = 'workq_filters_reset_v1'
const WORKQ_FILTERS_RESET_VERSION_VALUE = '1.1'

/**
 * One-time migration: clears every existing user's stale work-queue filters once per browser.
 * Bump WORKQ_FILTERS_RESET_VERSION_VALUE to force the reset to run again in the future.
 */
export function runOneTimeWorkQFilterReset() {
    try {
        if (getItemFromStorageSimple(WORKQ_FILTERS_RESET_VERSION) === WORKQ_FILTERS_RESET_VERSION_VALUE) {
            return
        }
        clearWorkQFilters()
        localStorage.setItem(WORKQ_FILTERS_RESET_VERSION, WORKQ_FILTERS_RESET_VERSION_VALUE)
    } catch (e) {
        // ignore
    }
}